import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

export default function ProblemGrid() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(360);
  const [visibleCount, setVisibleCount] = useState(3);
  const containerRef = useRef(null);
  const carouselRef = useRef(null);

  useEffect(() => {
    function updateDimensions() {
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        if (width < 640) {
          setVisibleCount(1);
          setCardWidth(Math.min(width - 48, 320));
        } else if (width < 1024) {
          setVisibleCount(2);
          setCardWidth(340);
        } else {
          setVisibleCount(3);
          setCardWidth(380);
        }
      }
    }
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const problems = [
    {
      title: 'Stress',
      slug: 'stress',
      tagline: '“Jeg føler mig konstant på og kan ikke finde ro”',
      desc: 'Berolig nervesystemet, slip overansvaret og genopbyg dit mentale og kropslige overskud.',
      icon: 'fa-solid fa-bolt',
      color: 'bg-amber-500/10 text-amber-800',
    },
    {
      title: 'Angst',
      slug: 'angst',
      tagline: '“Min angst og uro styrer alt for meget af min hverdag”',
      desc: 'Få redskaber til at regulere panik, hjertebanken og uro i krop og tanker.',
      icon: 'fa-solid fa-wind',
      color: 'bg-emerald-500/10 text-emerald-800',
    },
    {
      title: 'Depression',
      slug: 'depression',
      tagline: '“Hverdagen føles tung, grå og uoverskuelig”',
      desc: 'Nærværende støtte til at bryde isolation, genfinde retning og tænde livsglæden igen.',
      icon: 'fa-solid fa-cloud-sun',
      color: 'bg-sky-500/10 text-sky-800',
    },
    {
      title: 'Lavt selvværd',
      slug: 'lavt-selvvaerd',
      tagline: '“Jeg kæmper med en konstant indre kritiker og tvivl”',
      desc: 'Lær at stå ved den, du er, sætte sunde grænser og mød dig selv med accept og styrke.',
      icon: 'fa-solid fa-heart',
      color: 'bg-rose-500/10 text-rose-800',
    },
    {
      title: 'Traumer',
      slug: 'traumer',
      tagline: '“Gamle oplevelser og chok sidder stadig fast i min krop”',
      desc: 'Transbiologisk Traumeterapi (TBT) til skånsom forløsning af chok, svigt og overvældelse.',
      icon: 'fa-solid fa-compass',
      color: 'bg-indigo-500/10 text-indigo-800',
    },
    {
      title: 'Sorg & krise',
      slug: 'sorg-og-krise',
      tagline: '“Jeg står i et pludseligt tab, en skilsmisse eller livskrise”',
      desc: 'Omsorgsfuld og tryg bearbejdning, så du kan bære tabet og genfinde fodfæstet i livet.',
      icon: 'fa-solid fa-feather',
      color: 'bg-stone-500/10 text-stone-800',
    },
    {
      title: 'Vaner: rygestop & sukkertrang',
      slug: 'hypnose-vaner',
      tagline: '“Jeg vil bryde rygetrang, sukkertrang eller automatiske mønstre”',
      desc: 'Målrettet hypnoterapi over 2-3 sessioner mod nikotinstop og uhensigtsmæssige vaner.',
      icon: 'fa-solid fa-sparkles',
      color: 'bg-teal-500/10 text-teal-800',
    },
  ];

  const maxIndex = Math.max(0, problems.length - visibleCount);

  const handlePrev = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  return (
    <section className="py-20 sm:py-24 bg-[#ECEEF1] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Contained Header with pagination & navigation aligned to header container */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 sm:mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#5A232E]/10 text-[#5A232E] text-xs font-semibold uppercase tracking-wider mb-4">
              <i className="fa-solid fa-circle-dot text-[10px] text-[#5A232E]" aria-hidden="true" />
              Problem før behandling
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#5A232E] tracking-tight leading-[1.15]">
              Hvad vil du gerne<br className="hidden sm:inline" /> have hjælp til?
            </h2>
            <p className="mt-4 text-base sm:text-lg text-[#6c7480] leading-relaxed">
              Du behøver ikke vide på forhånd, hvilken terapiform du har brug for. Tag udgangspunkt i det, der fylder i din hverdag lige nu – så finder vi den rette vej sammen.
            </p>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center gap-2 shrink-0 pb-1">
            <button
              type="button"
              onClick={handlePrev}
              disabled={currentIndex === 0}
              aria-label="Forrige problemområde"
              className={`w-11 h-11 rounded-full flex items-center justify-center border transition-all ${
                currentIndex === 0
                  ? 'border-[#D9DDE2] text-[#6c7480]/40 cursor-not-allowed bg-transparent'
                  : 'border-[#5A232E]/30 text-[#5A232E] hover:bg-[#5A232E] hover:text-white hover:border-[#5A232E] cursor-pointer shadow-xs active:scale-95'
              }`}
            >
              <i className="fa-solid fa-arrow-left text-sm" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={currentIndex >= maxIndex}
              aria-label="Næste problemområde"
              className={`w-11 h-11 rounded-full flex items-center justify-center border transition-all ${
                currentIndex >= maxIndex
                  ? 'border-[#D9DDE2] text-[#6c7480]/40 cursor-not-allowed bg-transparent'
                  : 'border-[#5A232E] bg-[#5A232E] text-white hover:bg-[#431722] cursor-pointer shadow-sm active:scale-95'
              }`}
            >
              <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal Carousel: contained within max-w-7xl like the header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div
          ref={carouselRef}
          className="flex transition-transform duration-500 ease-out will-change-transform"
          style={{
            transform: `translateX(-${currentIndex * (cardWidth + 24)}px)`,
          }}
        >
          {problems.map((problem) => (
            <div
              key={problem.slug}
              className="shrink-0 mr-6"
              style={{ width: `${cardWidth}px` }}
            >
              <div className="h-full bg-white rounded-3xl border border-[#D9DDE2] hover:border-[#5A232E]/50 shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col justify-between group">
                {/* Visual Top Area with subtle organic shapes and problem tagline */}
                <div className="relative p-6 sm:p-7 bg-gradient-to-br from-[#ECEEF1] via-[#F1F4F8] to-[#E5E8EC] border-b border-[#D9DDE2]/60 overflow-hidden min-h-[110px] flex flex-col justify-center">
                  {/* Decorative background shapes */}
                  <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-[#5A232E]/5 pointer-events-none" />
                  <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-[#5A232E]/5 pointer-events-none" />

                  <div className="relative z-10">
                    <p className="text-xs font-medium italic text-[#5A232E] leading-relaxed">
                      {problem.tagline}
                    </p>
                  </div>
                </div>

                {/* Content Area */}
                <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="font-display text-2xl font-semibold text-[#5A232E] group-hover:text-[#431722] transition-colors mb-3">
                      {problem.title}
                    </h3>

                    <p className="text-sm text-[#6c7480] leading-relaxed mb-6 line-clamp-3">
                      {problem.desc}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-[#D9DDE2]/60 flex items-center justify-start">
                    <Link
                      to={`/${problem.slug}/`}
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#5A232E] hover:bg-[#431722] text-white text-xs font-semibold tracking-wide shadow-xs transition-all group-hover:shadow-md"
                    >
                      <span>Læs mere</span>
                      <i className="fa-solid fa-arrow-right text-[11px] group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
